import React, { useState } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { MdCheckCircle, MdEventNote, MdExpandLess, MdExpandMore, MdKeyboardArrowDown, MdKeyboardArrowUp, MdStar, MdTimer, MdWarning } from 'react-icons/md';
import { detectLinkInText, hasDollarInString, numberWithCommas, roundDecimalValue } from '../../../utils/utils';
import { MIN_EMI_AMOUNT, MIN_AUTO_DEBIT_AMOUNT, MIN_DIRECT_AMOUNT, CUSTOMER_PORTAL_BASE_URL } from '../../../../constants';
import { checkboxFeeTypesFetchGet, paymentPagesFeeTypesFetchGet } from '../../../api-client/api-gqadmin-client';
import { useQuery } from '../../../hooks';
import { gqSvcPaymentPageApiClient } from '../../../api-client/api-svcpp-client/axios';
import PG_AXIS from '../../../assets/images/pg_axis.png';
import PG_CASHFREE from '../../../assets/images/pg_cashfree.png';
import PG_EASEBUZZ from '../../../assets/images/pg_easebuzz.png';
import PG_HDFC from '../../../assets/images/pg_hdfc.png';
import PG_RAZORPAY from '../../../assets/images/pg_razorpay.png';
import EMI_Calendar from '../../../assets/icons/calendar-icon.svg';
import CustomButton from '../CustomButton/CustomButton';
import classes from './PaymentModeCard.module.scss';


const FinancingModeCollapsible = ({ data }) => {
    const [isOpen, setIsOpen] = useState(false);
    const toggleCard = (event) => { 
        event.stopPropagation();
        setIsOpen(!isOpen)
    };

    if (!data?.financing_mode) return null;
    
    switch (data?.financing_mode) {
        case 'MONTHLY-EMI':
            if (!data?.emi_repayment || data?.emi_repayment?.length === 0) return null;
            return (<section className={classes.CollapsibleCard}>
                <div className={classes.CardHeader} onClick={toggleCard}>
                    <span className={classes.Text}>EMI Repayments</span>
                    <span className={classes.Icon}>
                        {!isOpen ? 
                            <MdKeyboardArrowDown size='1.25rem' />
                        : <MdKeyboardArrowUp size='1.25rem' />}
                    </span>
                </div>
                <div className={`${classes.CardBody} ${isOpen ? classes.Open : ''}`}>
                    {/* <h5 className={classes.CardDetails__Title}>EMI Repayments</h5> */}
                    {data?.emi_repayment?.map((item, index) => (
                        <div key={`emi-repayment-${index}`} className={classes.ListItem}>
                            <span className={classes.Label}>
                                <span className={classes.Label__Text}>{item?.emi_month}</span>
                                <span className={classes.Label__Subtext}>{item?.emi_due_date}</span>
                            </span>
                            <span className={classes.Value}>{numberWithCommas(item?.emi_amount)}</span>
                        </div>
                    ))}
                </div>
            </section>)
        break;
        case 'AUTO-DEBIT':
            if (!data?.schedule_config || data?.schedule_config?.length === 0) return null;
            return (<section className={classes.CollapsibleCard}>
                <div className={classes.CardHeader} onClick={toggleCard}>
                    <span className={classes.Text}>Auto Debit Schedule</span>
                    <span className={classes.Icon}>
                        {!isOpen ? 
                            <MdKeyboardArrowDown size='1.25rem' />
                        : <MdKeyboardArrowUp size='1.25rem' />}
                    </span>
                </div>
                <div className={`${classes.CardBody} ${isOpen ? classes.Open : ''}`}>
                    {/* <h5 className={classes.CardDetails__Title}>Auto Debit Schedule</h5> */}
                    {data?.schedule_config?.map((item) => (
                        <div key={item?.ft_name} className={classes.ListItem}>
                            <span className={classes.Label}>
                                <span className={classes.Label__Text}>{item?.ft_name}</span>
                                <span className={classes.Label__Subtext}>{item?.due_date}</span>
                            </span>
                            <span className={classes.Value}>{numberWithCommas(item?.amount)}</span>
                        </div>
                    ))}
                </div>
            </section>)
        break;
        case 'DIRECT':
            if (!data?.pg_charges || data?.pg_charges?.length === 0) return null;
            return (<section className={classes.CollapsibleCard}>
                <div className={classes.CardHeader} onClick={toggleCard}>
                    <span className={classes.Text}>Extra Charges</span>
                    <span className={classes.Icon}>
                        {!isOpen ? 
                            <MdKeyboardArrowDown size='1.25rem' />
                        : <MdKeyboardArrowUp size='1.25rem' />}
                    </span>
                </div>
                <div className={`${classes.CardBody} ${isOpen ? classes.Open : ''}`}>
                    {/* <h5 className={classes.CardDetails__Title}>Extra Charges</h5> */}
                    {data?.pg_charges?.map((item, index) => (
                        <div key={`pg-charges-${index}`} className={classes.ListItem}>
                        <span className={classes.Label}>
                                <span className={classes.Label__Text}>{item?.text}</span>
                                <span className={classes.Label__Subtext}>{item?.subtext}</span>
                            </span>
                            <span className={classes.Value}>{numberWithCommas(item?.value)}</span>
                        </div>
                    ))}
                </div>
            </section>)
        break;
        default:
            return (<section className={classes.CollapsibleCard}>
                    <h3>NO Data Available!</h3>
                </section>)
            break;
    }
}

// Fetch session data by code
const fetchSessionData = async (code) => {
    return await gqSvcPaymentPageApiClient({
        method: 'GET',
        url: `/v1/misc/session/fetch?session_code=${code}`
    })
}

const FeeTypeDetailsCollapsible = ({ data, pageData, sbdCode, checkboxSessionCode, isCheckboxEnabled, isDynamicFtEnabled, dynamicFtSessionCode }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isFeeTypesLoading, setIsFeeTypesLoading] = useState(true);
    const [feeTypesData, setFeeTypesData] = useState([]);
    const query = useQuery();
    const toggleCard = async (event) => {
        event.stopPropagation();
        setIsOpen(!isOpen);
        if (isOpen) {
            setFeeTypesData([]);
            return;
        }

        let PPSC_FLOW=false;
        if (dynamicFtSessionCode || checkboxSessionCode || query.get('ppsc')) {
            const session_code = dynamicFtSessionCode || checkboxSessionCode || query.get('ppsc')
            const ppsc_data = await fetchSessionData(session_code);
            if (ppsc_data?.data?.status_code === 200
            ) {
                PPSC_FLOW=true;
            }
        }

        await fetchFeeTypeDetails({
            pp_slug: pageData?.payment_page_slug,
            ...(sbdCode && { sbd_code: sbdCode }),
            ...(query.get('ppsc') && PPSC_FLOW && { session_code: query.get('ppsc') }),
            ...(checkboxSessionCode && PPSC_FLOW && { session_code: checkboxSessionCode }),
            ...(dynamicFtSessionCode && PPSC_FLOW && { session_code: dynamicFtSessionCode }),
            isCheckboxEnabled: isCheckboxEnabled,
            ...(pageData?.dynamic_link && { dynamic_link: pageData?.dynamic_link })
        });
    }

    const fetchFeeTypeDetails = async (payload) => {
        try {
            setIsFeeTypesLoading(true);
            const { isCheckboxEnabled, pp_slug, ...restPayload } = payload;
            let paymentPagesFeeTypesFetchGetRes = null;
            if (isCheckboxEnabled) {
                paymentPagesFeeTypesFetchGetRes = await checkboxFeeTypesFetchGet({
                    pp_slug: pp_slug,
                    ...restPayload,
                    fg_discount_applicable: true,
                });
            } else {
                paymentPagesFeeTypesFetchGetRes = await paymentPagesFeeTypesFetchGet({
                    payment_page_slug: pp_slug,
                    ...restPayload,
                });
            }
            if (paymentPagesFeeTypesFetchGetRes?.data?.data?.length > 0) {
                setFeeTypesData(paymentPagesFeeTypesFetchGetRes?.data?.data);
            }
            setIsFeeTypesLoading(false);
        } catch (error) {
            console.error("fee type fetch", error);
            setFeeTypesData([]);
            setIsFeeTypesLoading(false);
        }
    }

    const renderFeeTypeDetailsContent = (FeeTypeDataList, cardData) => {
        const currentCardFeeGroupFeeTypeList = FeeTypeDataList.filter(element => element?.block_code === cardData?.code);
        return (<>
            <div className={classes.HeadingData}>
                <span className={classes.Label}>Total Payable fees</span>
                <span className={classes.Value}>Rs. {cardData?.fee_group?.amount?.toString()}</span>
            </div>
            {currentCardFeeGroupFeeTypeList?.[0]?.fee_types?.map(row => {
                const feeTypeLabel = row?.label;
                let feeTypeAmount = row?.base_amount?.toString();
                const dueDate = row?.due_date?.split('-').reverse().join('-') || '-';
                const discountAmount = row?.discount?.toString();
                // const discountRate = '10%';
                const fineAmount = row?.late_fee?.toString();
                const finalAmount = row?.payable_amount?.toString();
                const paidAmount = row?.amount_paid?.toString();
                const isPaid = row?.balance_outstanding === 0;
                const isEmiApplied = !isDynamicFtEnabled && row?.applied_for_emi;

                if (isCheckboxEnabled && row?.payable_amount <= 0 && row?.balance_outstanding >= 0) {
                    return null;
                }
                if (isDynamicFtEnabled && row?.dynamic_ft) {
                    feeTypeAmount = row?.amount?.toString();
                }
                
                return (<div className={classes.Row}>
                    <div className={classes.Column}>
                        <span className={classes.Label}>{feeTypeLabel}</span>
                        <span className={classes.Value}>Rs. {feeTypeAmount}</span>
                    </div>
                    <div className={classes.Column}>
                        <span className={classes.Label}>Due Date</span>
                        <span className={classes.Value}>{dueDate}</span>
                    </div>
                    <div className={classes.Column}>
                        <span className={classes.Label}>Discount</span>
                        <span className={`${classes.Value} ${classes.Discount}`}>Rs. {discountAmount} </span>
                    </div>
                    <div className={classes.Column}>
                        <span className={classes.Label}>Fine or Penalty</span>
                        <span className={`${classes.Value} ${classes.Fine}`}>Rs. {fineAmount}</span>
                    </div>
                    {
                        isEmiApplied 
                        ? <div className={`${classes.Column} ${classes.EmiApplied}`}>
                            <span className={classes.Label}>Included in</span>
                            <span className={classes.Value}>
                                <MdEventNote className={classes.Icon}/>
                                EMI plan
                            </span>
                        </div> 
                        : (!isDynamicFtEnabled && isPaid) 
                            ? feeTypeAmount === "0" 
                                ? <div className={`${classes.Column} ${classes.EmiApplied}`}>
                                    <span className={classes.NoPayableAmountLabel}>No Payable Amount</span>
                                </div> 
                                : <div className={`${classes.Column} ${classes.PaidAmount}`}>
                                    <span className={classes.Label}>
                                        <span className={classes.Icon}>&#10003;</span>
                                        Paid 
                                    </span>
                                    <span className={classes.Value}>Rs. {paidAmount}</span>
                                </div>
                            :  <>
                                {!row?.dynamic_ft && (row?.base_amount?.toString() == row?.amount_paid?.toString()) ? 
                                    <div className={`${classes.Column} ${classes.PaidAmount}`}>
                                        <span className={classes.Label}>
                                            <span className={classes.Icon}>&#10003;</span>
                                            Paid 
                                        </span>
                                        <span className={classes.Value}>Rs. {paidAmount}</span>
                                    </div>
                                : <div className={`${classes.Column} ${classes.Total}`}>
                                    <span className={classes.Label}>Final Amount</span>
                                    <span className={classes.Value}>Rs. {finalAmount}</span>
                                </div>}
                            </>
                    }
                </div>)
            })}
        </>)
    }

    return (
        <section className={`${classes.CollapsibleCard} ${isOpen ? classes.Active : ''}`}>
            <div className={classes.CardHeader} onClick={toggleCard}>
                <span className={classes.Text}>Fee Structure Details</span>
                <span className={classes.Icon}>
                    {!isOpen ? 
                        <MdKeyboardArrowDown size='1.25rem' />
                    : <MdKeyboardArrowUp size='1.25rem' />}
                </span>
            </div>

            <div className={`${classes.CardBody} ${isOpen ? classes.Open : ''}`}>
                {(isOpen && isFeeTypesLoading) ? 
                    <p style={{ textAlign: 'center' }}>Loading...</p>
                    : (feeTypesData?.length > 0 ? 
                        (renderFeeTypeDetailsContent(feeTypesData, data)) 
                        : (<p style={{ textAlign: 'center' }}>No Data Available</p>)
                    )
                }
            </div>
        </section>
    )
}

const CollapsibleData = ({ data, pageData, sbdCode, checkboxSessionCode, isCheckboxEnabled, isDynamicFtEnabled, dynamicFtSessionCode }) => {
    let showFeeTypeDetails = false;
    if (data?.show_fee_types_data) {
        showFeeTypeDetails = true;
    }
    return (
        <>
            <FinancingModeCollapsible data={data} />
            {showFeeTypeDetails && 
                <FeeTypeDetailsCollapsible 
                    data={data} 
                    pageData={pageData} 
                    sbdCode={sbdCode} 
                    checkboxSessionCode={checkboxSessionCode} 
                    isCheckboxEnabled={isCheckboxEnabled}
                    isDynamicFtEnabled={isDynamicFtEnabled}
                    dynamicFtSessionCode={dynamicFtSessionCode}
                />
            }
        </>
    )
}

const PaymentModeCard = (props) => {
    const { 
        data,
        pageData,
        index,
        studentId,
        customerNumber,
        sbdCode,
        // commonPayloadData,
        // wrapperApiResData,
        selectedPaymentMode,
        handlePaymentModeSelect,
        checkboxSessionCode,
        dynamicFtSessionCode,
    } = props;
    if (data?.financing_mode === '') return null;
    if (data?.fee_group?.amount === 0 && data?.financing_mode === 'MONTHLY-EMI' && !data?.fee_group?.applied_for_emi) return null;
    if (data?.fee_group?.amount === 0 && data?.financing_mode === 'AUTO-DEBIT') return null;
    if (data?.fee_group?.amount === 0 && data?.financing_mode === 'DIRECT') return null;

    const [showCardDetails, setShowCardDetails] = useState(false);

    const isDynamicLinkPage = pageData.hasOwnProperty('dynamic_link') ? pageData?.dynamic_link : false;
    const isEmiActive = data?.fee_group?.applied_for_emi && data?.financing_mode === 'MONTHLY-EMI';
    const emiMinAmount = pageData?.emi_min_amount ?? MIN_EMI_AMOUNT;
    const emiMaxAmount = pageData?.emi_max_amount;

    const cardType = data?.financing_mode || '';
    const isCardSelected = data?.code === selectedPaymentMode?.code;
    const cardTitle = data?.title || '';
    const cardSubtitle = data?.subtitle || '';
    const cardDescription = data?.description || '';
    const isRecommended = data?.is_recommended;
    const isRecommendedText = data?.is_recommended_text;
    const ribbonText = data?.ribbon_text || '';
    let autodebitStartDate='';
    const isCardDisabled = 
        (cardType === 'MONTHLY-EMI' && ((data?.fee_group?.amount < emiMinAmount && !isEmiActive) || data?.fee_group?.amount > emiMaxAmount)) || 
        (cardType === 'AUTO-DEBIT' && data?.fee_group?.amount < MIN_AUTO_DEBIT_AMOUNT) || 
        (cardType === 'DIRECT' && data?.fee_group?.amount < MIN_DIRECT_AMOUNT);
    const disabledCardMessage = 
        (cardType === 'MONTHLY-EMI' && 
            (data?.fee_group?.amount > emiMaxAmount 
                ? `The Monthly Installment option is not available for amounts above Rs.${numberWithCommas(emiMaxAmount)}` 
                : `The Monthly Installment option is not available for amounts below Rs.${numberWithCommas(emiMinAmount)}`)) || 
        (cardType === 'AUTO-DEBIT' && `The Auto Debit option is not available for amounts below Rs.${numberWithCommas(MIN_AUTO_DEBIT_AMOUNT)}`) || 
        (cardType === 'DIRECT' && "") || `The Payment Gateway option is not available for amounts below ${numberWithCommas(MIN_DIRECT_AMOUNT)}`;
    let cardAmount = data?.fee_group?.amount;
    if (cardType === 'MONTHLY-EMI') {
        if (data?.fee_group?.advance_emi_amount === 0) {
            cardAmount = 0;
        } else {
            cardAmount = data?.fee_group?.emi_amount || data?.fee_group?.amount;
        }
    }
    if (cardType === 'AUTO-DEBIT' && data?.schedule_config?.length > 0) {
        let totalAdAmount=0;
        let payableDate="";
        data?.schedule_config?.map((item) => {
            if (item?.amount?.toString() !== '0' && !payableDate) {
                payableDate = item?.due_date;
            }
            if (item?.due_date === payableDate 
                && item?.amount?.toString()) {
                totalAdAmount += item?.amount;
            }
        })

        autodebitStartDate = payableDate;
        cardAmount = roundDecimalValue(totalAdAmount);
    }
    const cardAmountSubText = data?.fee_group?.amount_sub_text ? data?.fee_group?.amount_sub_text : '';
    const startDate = data?.start_date ? data?.start_date : '';
    const infoText = data?.info_text || '';
    const optionsString = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let hasCollapsibleData = false;
    if (cardType === 'MONTHLY-EMI' && (data?.emi_repayment?.length || data?.show_fee_types_data)) {
        hasCollapsibleData = true;
    } else if (cardType === 'AUTO-DEBIT' && (data?.schedule_config?.length || data?.show_fee_types_data)) {
        hasCollapsibleData = true;
    } else if (cardType === 'DIRECT' && (data?.pg_charges?.length || data?.show_fee_types_data)) {
        hasCollapsibleData = true;
    }

    let pgVendorImage = '';
    if (cardType === 'DIRECT' && data?.pg_vendor) {
        const allVendorImages = {
            "HDFC-COLLECTNOW-RAZORPAY": PG_HDFC,
            "HDFC-VAS-RAZORPAY": PG_HDFC,
            "UNIPG": PG_AXIS,
            "CASHFREE": PG_CASHFREE,
            "EASEBUZZ": PG_EASEBUZZ,
            "RAZORPAY": PG_RAZORPAY,
        }
        pgVendorImage = allVendorImages[data?.pg_vendor] || '';
    }

    const handleOnCardSelect = (cardData) => {
        if (!cardData || (selectedPaymentMode?.code === cardData?.code)) return;
        handlePaymentModeSelect(cardData);
    }
    const handleCardToggle = (event) => {
        event.stopPropagation();
        setShowCardDetails(!showCardDetails);
    };

    const renderCardDate = () => {
        if (cardType === 'MONTHLY-EMI' && startDate) {
            return (<div className={classes.CardDateWrap}>
                <span className={classes.DateIcon}>
                    <MdTimer />
                </span>
                <span className={classes.CardDate}>
                    {data?.fee_group?.advance_emi_amount === 0 ? 
                        `EMI of ${numberWithCommas(data?.fee_group?.emi_amount)} starts on `
                    : `EMI starts on `}
                    <span className={classes.Date}>{startDate}</span>
                </span>
            </div>)
        }

        if (cardType === 'AUTO-DEBIT' && autodebitStartDate) {
            return (<div className={classes.CardDateWrap}>
                <span className={classes.DateIcon}>
                    <MdTimer />
                </span>
                <span className={classes.CardDate}>
                    {`Auto-debit starts on `}
                    <span className={classes.Date}>{autodebitStartDate}</span>
                </span>
            </div>)
        }

        // if (cardType === 'DIRECT') {
        //     return (<div className={classes.CardDateWrap}>
        //         <span className={`${classes.CardDate} ${classes.Hidden}`}>
        //             Payable Today 
        //         </span>
        //     </div>)
        // }

        return null;
    }

    const renderLinkText = (text, appendParams) => {
        if (!text) return null;
        return detectLinkInText(text, appendParams)
    }

    const handleViewEmiApplication = () => {
        window.open(`${CUSTOMER_PORTAL_BASE_URL}/?m=${customerNumber}`, '_blank');
    }

    if(!isDynamicLinkPage && isEmiActive) {
        return(
            <div className={classes.EmiApplied__Wrapper}>
                <div className={classes.EmiHeader}>
                    <div className={classes.HeaderWrap}>
                        <img className={classes.Icon} src={EMI_Calendar} alt="EMI_Calendar" />
                        <div className={classes.TitleWrap}>
                            <span className={classes.Title}>You have registered for a Monthly payment plan</span>
                            {   
                                hasCollapsibleData 
                                ? (<button
                                        className={classes.DetailsToggleButton__Desktop}
                                        onClick={handleCardToggle}
                                    >
                                        {
                                            showCardDetails 
                                            ? (<>Less Details <MdExpandLess size='1rem' /> </>)
                                            : (<>More Details <MdExpandMore size='1rem' /> </>)
                                        }
                                    </button>)
                                : null
                            }
                        </div>
                    </div>
                    <div className={classes.ActionWrap}>
                        <CustomButton
                            type="button"
                            text="View application"
                            onClick={() => handleViewEmiApplication()}
                            title="View application"
                            variant="outline"
                            className={classes.ViewButton}
                        />
                    </div>
                </div>
                {hasCollapsibleData ? 
                    (<button
                        className={classes.DetailsToggleButton__Mobile}
                        onClick={handleCardToggle}
                    >
                        {showCardDetails ? 
                        (<>Less Details <MdExpandLess size='1rem' /> </>)
                        : (<>More Details <MdExpandMore size='1rem' /> </>)}
                    </button>)
                : null}
                <div className={`${classes.CardDetails} ${showCardDetails ? classes.Show : ''}`}>
                    <CollapsibleData 
                        data={data} 
                        pageData={pageData}
                        sbdCode={sbdCode}
                        checkboxSessionCode={checkboxSessionCode}
                        isCheckboxEnabled={pageData?.is_checkbox_enabled}
                        isDynamicFtEnabled={pageData?.dynamic_link}
                        dynamicFtSessionCode={dynamicFtSessionCode}
                    />
                </div>
            </div>
        )
    }

    return (
        <div 
            className={`${classes.PaymentModeCard__Wrapper} ${isCardDisabled ? classes.Disabled : ''} ${isCardSelected ? classes.Selected : ''}`}
            onClick={() => handleOnCardSelect(data)}
        >
            <div 
                className={`${classes.PaymentModeCard}`}
            >
                <span className={classes.OptionBadge}>Option {!isDynamicLinkPage && isEmiActive ? optionsString[index+1] : optionsString[index]}</span>

                <div className={classes.Card__Left}>
                    <div className={classes.RadioIndicatorWrap}>
                        <span className={classes.RadioIndicator}></span>
                    </div>

                    <div className={classes.CardDetails__Left}>
                        {cardTitle ? (<h3 className={classes.CardTitle}>{cardTitle}</h3>) : null}
                        {cardSubtitle ? 
                            (<p 
                                className={classes.CardSubtitle}
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderLinkText(cardSubtitle)) }}
                            />) 
                        : null}
                        
                        {data?.tags?.length !== 0 ? 
                            (<div className={classes.TagsWrap__Desktop}>
                                {data?.tags?.map((tag, index) => {
                                    return (<span key={tag} className={classes.Tag}>{tag}</span>)
                                })}
                            </div>)
                        : null}

                        {hasCollapsibleData ? 
                            (<button
                                className={classes.DetailsToggleButton__Desktop}
                                onClick={handleCardToggle}
                            >
                                {showCardDetails ? 
                                (<>Less Details <MdExpandLess size='1rem' /> </>)
                                : (<>More Details <MdExpandMore size='1rem' /> </>)}
                            </button>)
                        : null}
                    </div>
                </div>

                <div className={classes.Card__Right}>
                    {isRecommended ? 
                        <>
                            {!isRecommendedText ? 
                                (<div className={classes.TopRecommendedBadge}>
                                    <span className={classes.BadgeIcon}>
                                        <MdStar />
                                    </span>
                                    <span className={classes.BadgeText}> Most Popular </span>
                                </div>)
                            : ((<div className={classes.TopRecommendedBadge}>
                                <span className={classes.BadgeText}>{isRecommendedText}</span>
                            </div>))}
                        </>
                    : null}
                    <div className={classes.CardDetails__Right}>
                        <p className={classes.CardAmountSubText}>
                            {cardType === 'MONTHLY-EMI' 
                                ? (cardAmount === 0 ? 'Payable today' : 'Pay as low as' ) 
                            : ''} 
                        </p>
                        {cardAmount?.toString() ? 
                            (<p className={classes.CardAmount}>
                                {numberWithCommas(cardAmount)}
                                {cardType === 'MONTHLY-EMI' 
                                    ? (cardAmount !== 0 ? <span className={classes.CardAmount__SubText}>/month</span> : '' ) 
                                : ''}
                            </p>)
                        : null}
                        {renderCardDate()}
                        {/* <p className={classes.CardDiscount}>10% discount applied</p> */}
                        {cardType === 'DIRECT' && pgVendorImage
                            ? <div className={classes.PgVendorImage}>
                                <span>payment Via</span>
                                <img src={pgVendorImage} />
                            </div>
                        : ''}
                    </div>
                </div>

                {data?.tags?.length !== 0 ? 
                    (<div className={classes.TagsWrap__Mobile}>
                        {data?.tags?.map((tag, index) => {
                            return (<span key={tag} className={classes.Tag}>{tag}</span>)
                        })}
                    </div>)
                : null}

                {hasCollapsibleData ? 
                    (<button
                        className={classes.DetailsToggleButton__Mobile}
                        onClick={handleCardToggle}
                    >
                        {showCardDetails ? 
                        (<>Less Details <MdExpandLess size='1rem' /> </>)
                        : (<>More Details <MdExpandMore size='1rem' /> </>)}
                    </button>)
                : null}
            </div>

            <div className={`${classes.CardDetails} ${showCardDetails ? classes.Show : ''}`}>
                <CollapsibleData 
                    data={data} 
                    pageData={pageData}
                    sbdCode={sbdCode}
                    checkboxSessionCode={checkboxSessionCode}
                    isCheckboxEnabled={pageData?.is_checkbox_enabled}
                    isDynamicFtEnabled={pageData?.dynamic_link}
                    dynamicFtSessionCode={dynamicFtSessionCode}
                />
            </div>
            
            {infoText ? 
                (<div 
                    className={classes.InfoText}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderLinkText(infoText, {
                        ...(data?.custom_link_params?.includes('student_id') 
                            && studentId 
                            && !hasDollarInString(studentId)
                            && { sid: studentId }),
                        ...(data?.custom_link_params?.includes('sbd_code') 
                            && sbdCode
                            && { sbdc: sbdCode}),
                        ...(data?.custom_link_params?.includes('mobile') 
                            && customerNumber 
                            && !hasDollarInString(customerNumber)
                            && { m: customerNumber }),
                    })) }}
                />)
            : null}

            {isCardDisabled ? 
                (<div className={classes.DisabledCardNote}>
                    <MdWarning fontSize='1rem' />
                    {disabledCardMessage}
                </div>)
            : null}
        </div>
    )
}

PaymentModeCard.propTypes = {}

export default PaymentModeCard